import { useState } from "react"

import { useTranslation } from "react-i18next"

import { Check, Plus, Trash2, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

const HEX_REGEX = /^#[0-9a-fA-F]{6}$/

type ColorPickerProps = {
  color: string
  onChange: (color: string) => void
  onRemove?: () => void
}

export function ColorPicker({ color, onChange, onRemove }: ColorPickerProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.styleTemplates.creator" })
  const [hexInput, setHexInput] = useState(color)
  const [localColor, setLocalColor] = useState(color)
  const [open, setOpen] = useState(false)
  const isAddNew = !onRemove
  const nativeValue = HEX_REGEX.test(localColor) ? localColor : "#000000"

  const handleColorChange = (hex: string) => {
    setLocalColor(hex)
    setHexInput(hex)
  }

  const handleHexChange = (value: string) => {
    const hex = value.startsWith("#") ? value : `#${value}`
    setHexInput(hex)
    if (HEX_REGEX.test(hex)) {
      setLocalColor(hex)
    }
  }

  const handleApply = () => {
    if (HEX_REGEX.test(localColor)) {
      onChange(localColor)
    }
    setOpen(false)
  }

  const handleCancel = () => {
    setOpen(false)
  }

  if (isAddNew) {
    return (
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o)
          if (o) {
            setHexInput("#000000")
            setLocalColor("#000000")
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            aria-label={t("addColor")}
            className="border-input flex h-8 w-8 items-center justify-center rounded-full border shadow-sm transition-colors hover:opacity-80"
            type="button"
          >
            <Plus className="text-muted-foreground h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 space-y-3">
          <input
            className="h-10 w-full cursor-pointer rounded border-none"
            type="color"
            value={nativeValue}
            onChange={(e) => handleColorChange(e.target.value)}
          />
          <Input
            maxLength={7}
            placeholder="#000000"
            value={hexInput}
            onChange={(e) => handleHexChange(e.target.value)}
          />
          <div className="flex gap-2">
            <Button className="flex-1" size="sm" onClick={handleApply}>
              <Check className="mr-1.5 h-3.5 w-3.5" />
              {t("applyColor")}
            </Button>
            <Button className="flex-1" size="sm" variant="outline" onClick={handleCancel}>
              <X className="mr-1.5 h-3.5 w-3.5" />
              {t("cancelColor")}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (o) {
          setHexInput(color)
          setLocalColor(color)
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          aria-label={`${color} — ${t("editColor")}`}
          className="h-8 w-8 cursor-pointer rounded-full border shadow-sm transition-opacity hover:opacity-80"
          style={{ backgroundColor: color }}
          type="button"
        />
      </PopoverTrigger>
      <PopoverContent className="w-56 space-y-3">
        <div className="flex items-center gap-3">
          <div
            className="h-10 w-10 shrink-0 rounded-full border-2 shadow-sm"
            style={{ backgroundColor: localColor }}
          />
          <span className="font-mono text-sm">{localColor}</span>
        </div>
        <input
          className="h-10 w-full cursor-pointer rounded border-none"
          type="color"
          value={nativeValue}
          onChange={(e) => handleColorChange(e.target.value)}
        />
        <Input
          maxLength={7}
          placeholder="#000000"
          value={hexInput}
          onChange={(e) => handleHexChange(e.target.value)}
        />
        <div className="flex gap-2">
          <Button className="flex-1" size="sm" onClick={handleApply}>
            <Check className="mr-1.5 h-3.5 w-3.5" />
            {t("applyColor")}
          </Button>
          <Button className="flex-1" size="sm" variant="outline" onClick={handleCancel}>
            <X className="mr-1.5 h-3.5 w-3.5" />
            {t("cancelColor")}
          </Button>
        </div>
        <Button className="w-full" size="sm" variant="destructive" onClick={onRemove}>
          <Trash2 className="mr-2 h-3.5 w-3.5" />
          {t("removeColor")}
        </Button>
      </PopoverContent>
    </Popover>
  )
}
